import { Typography } from "@mui/material";
import type {
  GridAggregationFunction,
  GridColDef,
  GridGroupingColDefOverride,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useMemo } from "react";
import type { TabularDataCell } from "../../../../../../api/types/svTypes.ts";
import useAgingDetailContext from "../contexts/AgingDetailContext.tsx";
import { FIRST_COLUMN_GUID, PERIOD_GUID, type AgingDetailGridRow } from "./AgingDetailTable.types.ts";
import DataCell from "./DataCell.tsx";
import FooterDataCell from "./FooterDataCell.tsx";
import FooterLabelCell from "./FooterLabelCell.tsx";
import GroupingCell from "./GroupingCell.tsx";

interface Props {
  onDrillDown?: (cell: TabularDataCell) => void;
}

const customAggregation: GridAggregationFunction<number, number> = {
  label: "",
  apply: () => 0,
  columnTypes: ["string"],
};

const aggregationFunctions: Record<string, GridAggregationFunction<number, number>> = {
  custom: customAggregation,
};

const AgingDetailTable = ({ onDrillDown }: Props) => {
  const { report, sortModel, onSortModelChange } = useAgingDetailContext();
  const apiRef = useGridApiRef();

  const groupingModel = useMemo(() => [PERIOD_GUID], []);

  const columnVisibilityModel = useMemo(() => ({ [PERIOD_GUID]: false, [FIRST_COLUMN_GUID]: false }), []);

  const aggregationModel = useMemo(() => {
    const model: Record<string, string> = {};
    for (const col of report?.columns ?? []) {
      if (col.guid.startsWith("detail_")) {
        model[col.guid] = "custom";
      }
    }
    return model;
  }, [report?.columns]);

  const getAggregationPosition = useCallback((): "footer" | "inline" | null => {
    return "footer";
  }, []);

  const getGroupColumnDef = useCallback((): GridGroupingColDefOverride<AgingDetailGridRow> => {
    return {
      hideDescendantCount: false,
      headerName: "Date",
      leafField: FIRST_COLUMN_GUID,
      sortable: true,
      aggregable: false,
      disableColumnMenu: true,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams<AgingDetailGridRow>) => {
        const { rowNode } = params;

        // Footer rows: show total label
        if (isFooterRow(params.id)) {
          return <FooterLabelCell params={params} />;
        }

        // Group header rows: expand/collapse + label + count
        if (rowNode.type === "group") {
          return <GroupingCell rowNode={rowNode} params={params} />;
        }

        // Leaf rows: show the date value
        if (rowNode.type === "leaf") {
          const dateValue = (params.row as AgingDetailGridRow)?.data?.[FIRST_COLUMN_GUID]?.formattedValue ?? "";
          return (
            <Typography variant="body1" sx={{ ml: 5 }}>
              {dateValue}
            </Typography>
          );
        }

        return null;
      },
    };
  }, []);

  const columns = useMemo<GridColDef<AgingDetailGridRow>[]>(() => {
    return (report?.columns ?? []).map((col) => {
      const isMeasure = col.guid.startsWith("detail_");
      const isGroupingColumn = col.guid === PERIOD_GUID;
      const isEntityName = col.guid === "EntityName";

      const colDef = {
        field: col.guid,
        headerName: col.title,
        minWidth: isMeasure ? 130 : isEntityName ? 200 : 140,
        flex: 1,
        sortable: true,
        disableColumnMenu: true,
        headerAlign: (isMeasure ? "right" : "left") as "left" | "right",
        align: (isMeasure ? "right" : "left") as "left" | "right",
        aggregable: isMeasure,
        ...(isGroupingColumn && {
          groupingValueGetter: (_value: unknown, row: AgingDetailGridRow) => row.data?.[col.guid]?.value,
        }),
        valueGetter: (_value: unknown, row: AgingDetailGridRow) => row.data?.[col.guid] ?? null,
        sortComparator: (v1: TabularDataCell | null, v2: TabularDataCell | null) => {
          const a = v1?.value;
          const b = v2?.value;
          if (a == null && b == null) return 0;
          if (a == null) return 1;
          if (b == null) return -1;
          if (typeof a === "number" && typeof b === "number") return a - b;
          return String(a).localeCompare(String(b));
        },

        renderCell: (params: GridRenderCellParams<AgingDetailGridRow>) => {
          const { rowNode } = params;

          // Only leaf rows render actual data
          if (rowNode.type === "leaf") {
            return <DataCell params={params} isMeasure={isMeasure} onDrillDown={onDrillDown} />;
          }

          // Footer rows (group subtotals + grand total): show aggregation values for measures
          if (isMeasure && isFooterRow(params.id)) {
            return (
              <FooterDataCell
                params={params}
                col={col}
                groupTotals={report?.groupTotals}
                grandTotals={report?.totals ?? null}
                onDrillDown={onDrillDown}
              />
            );
          }

          if (isGroupingColumn && isGroupHeaderRow(params.id)) {
            return params.formattedValue;
          } else {
            return null;
          }
        },
      };

      return colDef as GridColDef<AgingDetailGridRow>;
    });
  }, [report, onDrillDown]);

  const gridRows = useMemo<AgingDetailGridRow[]>(() => {
    return (report?.rows ?? []).map((row) => ({
      id: row.id,
      data: row.data,
      [PERIOD_GUID]: row.data[PERIOD_GUID]?.formattedValue ?? "",
    }));
  }, [report?.rows]);

  const hasRows = gridRows.length > 0;

  return (
    <DataGridPremium<AgingDetailGridRow>
      apiRef={apiRef}
      rows={gridRows}
      columns={columns}
      columnVisibilityModel={columnVisibilityModel}
      disableRowSelectionOnClick
      hideFooter
      columnHeaderHeight={36}
      rowHeight={36}
      showCellVerticalBorder
      showColumnVerticalBorder
      pinnedColumns={{ left: [] }}
      rowGroupingModel={groupingModel}
      rowGroupingColumnMode="single"
      groupingColDef={getGroupColumnDef}
      aggregationModel={aggregationModel}
      aggregationFunctions={aggregationFunctions}
      getAggregationPosition={getAggregationPosition}
      defaultGroupingExpansionDepth={-1}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      sx={(theme) => ({
        border: 1,
        borderColor: "divider",
        borderRadius: 1.5,
        "& .MuiDataGrid-columnHeaderTitle": { color: "text.secondary", ...theme.typography.caption },
        "& .MuiDataGrid-filler": { borderTop: hasRows ? `1px solid ${theme.palette.divider}` : "none" },
        "[data-id^=auto-generated-group-footer][role=row]": { bgcolor: theme.palette.background.grey },
        '[data-id^=auto-generated-row-][role=row] .MuiDataGrid-cell:not([data-field="__row_group_by_columns_group__"]):not([data-field="detail_OpenBalance"])':
          { borderRight: "none" },
      })}
    />
  );
};

function isFooterRow(rowId: GridRenderCellParams["id"]): boolean {
  return String(rowId).startsWith("auto-generated-group-footer");
}

function isGroupHeaderRow(rowId: GridRenderCellParams["id"]): boolean {
  return String(rowId).startsWith("auto-generated-row");
}

export default AgingDetailTable;
